import { type ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Disclaimer } from '@repo/design-system-kit/components/disclaimer/disclaimer';
import { SelectorCheckbox } from '@repo/design-system-kit/components/selector-checkbox/selector-checkbox';
import { BackButton } from 'register-partners-js/react/components/back-button/back-button';
import { KYB_NATURE_OF_OPERATIONS_OPTIONS } from '../../../constants/organization';
import styles from './nature-of-ops.module.css';
import {
  useDougsPartnerName,
  useIsCobrandedDougs,
} from 'register-partners-js/react/hooks/use-is-cobranded-dougs';
import { ResponsiveCta } from '@repo/shared-kit/components/responsive-cta/responsive-cta';

const FORM_ID = 'nature-of-ops-form';

interface Organization {
  natureOfOperations?: string[];
  legalCountry: string;
}

interface FormProps {
  organization: Organization;
  onSubmit: (sortedNatureOfOperations: string[]) => void;
  goBack: () => void;
  isSubmitting: boolean;
}

interface NatureOfOpsOption {
  id: (typeof KYB_NATURE_OF_OPERATIONS_OPTIONS)[keyof typeof KYB_NATURE_OF_OPERATIONS_OPTIONS];
  label: string;
}

export function NatureOfOps({
  organization,
  onSubmit,
  goBack,
  isSubmitting,
}: FormProps): ReactNode {
  const { formatMessage } = useIntl();
  const isCobrandedDougs = useIsCobrandedDougs();
  const dougsDisplayName = useDougsPartnerName();

  const [natureOfOperations, setNatureOfOperations] = useState<string[]>(
    organization.natureOfOperations ?? []
  );
  const [showNatureOfOpsError, setShowNatureOfOpsError] = useState(false);

  const legalCountry = organization.legalCountry;

  const {
    INVOICING,
    CORE_BANKING,
    SPEND_MANAGEMENT,
    BOOK_KEEPING,
    CASHFLOW_MANAGEMENT,
    REMUNERATION,
    PAYROLL_REIMBURSEMENT,
  } = KYB_NATURE_OF_OPERATIONS_OPTIONS;

  const natureOfOpsOptions: NatureOfOpsOption[] = [
    {
      id: CORE_BANKING,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.core-banking' }),
    },
    {
      id: INVOICING,
      label: formatMessage(
        { id: 'partner-register-kyb.nature-of-ops.invoicing' },
        { legalCountry }
      ),
    },
    {
      id: CASHFLOW_MANAGEMENT,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.cash-flow-management' }),
    },
    {
      id: SPEND_MANAGEMENT,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.spend-management.team' }),
    },
    {
      id: PAYROLL_REIMBURSEMENT,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.spend-management.payroll' }),
    },
    {
      id: BOOK_KEEPING,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.book-keeping' }),
    },
    {
      id: REMUNERATION,
      label: formatMessage({ id: 'partner-register-kyb.nature-of-ops.account-remuneration' }),
    },
  ];

  const toggleOption = (optionId: string): void => {
    setNatureOfOperations(prev =>
      prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
    );
    setShowNatureOfOpsError(false);
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();

    if (!natureOfOperations.length) {
      setShowNatureOfOpsError(true);
      return;
    }

    const sortedNatureOfOperations = [...natureOfOperations].sort();
    onSubmit(sortedNatureOfOperations);
  };

  return (
    <div>
      <BackButton
        className={styles.backButton}
        data-test-nature-of-ops-form-back-button
        isDisabled={isSubmitting}
        onPress={goBack}
      />
      <form data-test-nature-of-ops id={FORM_ID} onSubmit={handleSubmit}>
        <h1 className="heading-section mb-16" data-test-nature-of-ops-title>
          {isCobrandedDougs ? (
            <FormattedMessage
              id="partner-register-kyb.nature-of-ops.partner.title"
              values={{ partner: dougsDisplayName }}
            />
          ) : (
            <FormattedMessage id="partner-register-kyb.nature-of-ops.title" />
          )}
        </h1>
        <h2 className={cx('body-standard', styles.subtitle)} data-test-nature-of-ops-subtitle>
          <FormattedMessage id="partner-register-kyb.nature-of-ops.subtitle" />
        </h2>
        <div className={showNatureOfOpsError ? 'mb-24' : 'mb-32'}>
          {natureOfOpsOptions.map(option => (
            <SelectorCheckbox
              className={styles.selectorItem}
              data-test-nature-of-ops-selector-checkbox={option.id}
              isDisabled={isSubmitting}
              isSelected={natureOfOperations.includes(option.id)}
              key={option.id}
              onChange={(): void => {
                toggleOption(option.id);
              }}
              title={option.label}
            />
          ))}
        </div>
        {showNatureOfOpsError ? (
          <Disclaimer.Inline
            className="body-standard mb-32"
            data-has-error={showNatureOfOpsError}
            data-test-nature-of-ops-error-disclaimer=""
            level="error"
          >
            <FormattedMessage id="partner-register-kyb.nature-of-ops.error-message" />
          </Disclaimer.Inline>
        ) : null}
      </form>

      <ResponsiveCta
        data-test-nature-of-ops-form-submit-button=""
        className="mb-32"
        form={FORM_ID}
        isPending={isSubmitting}
        type="submit"
        label={formatMessage({ id: 'generic.continue.cta' })}
      />
    </div>
  );
}
